
import { RefObject, useEffect } from 'react';

interface TrapFocusProps {
  modalRef: RefObject<HTMLElement>;
  iframeRef: RefObject<HTMLIFrameElement>;
  customEvent?: string;
  onClose?: () => void;
}

export const useTrapFocus = ({ modalRef, iframeRef, customEvent, onClose }: TrapFocusProps) => {
    const trapFocus = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;

        const iframe = iframeRef.current;
        const modalContainer = modalRef.current;
        if (!iframe || !modalContainer) return;

        try {
            const iframeDoc = iframe.contentWindow?.document || null;
            const modalFocusableElements = modalContainer.querySelectorAll<HTMLElement>(
                'button:not([disabled]), [href]:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
            );
            /* eslint-disable no-undef */
            let iframeFocusableElements: NodeListOf<HTMLElement> = [] as any;
            if (iframeDoc) {
                iframeFocusableElements = iframeDoc.querySelectorAll<HTMLElement>(
                    'button:not([disabled]), [href]:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
                );
            }
            const allFocusableElements: HTMLElement[] = [
                ...Array.from(modalFocusableElements),
                ...Array.from(iframeFocusableElements),
            ];
            if (allFocusableElements.length === 0) return;
            const firstElement = allFocusableElements[0];
            const lastElement = allFocusableElements[allFocusableElements.length - 1];
            let currentActiveElement: Element | null = document.activeElement;
            if (document.activeElement === iframe && iframeDoc) {
                currentActiveElement = iframeDoc.activeElement;
            }
            const isCurrentElementInModal =
                allFocusableElements.includes(currentActiveElement as HTMLElement) ||
                (currentActiveElement && modalContainer.contains(currentActiveElement));
            if (!isCurrentElementInModal) {
                e.preventDefault();
                firstElement.focus();
                return;
            }
            if (e.shiftKey && currentActiveElement === firstElement) {
                e.preventDefault();
                lastElement.focus();
            } else if (!e.shiftKey && currentActiveElement === lastElement) {
                e.preventDefault();
                firstElement.focus();
            }
        } catch (error) {
            e.preventDefault();
            console.warn('Focus trap fallback:', error);
        }
    };

    const globalFocusTrap = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;
        const modalContainer = modalRef.current;
        if (!modalContainer) return;
        const isOutsideModal =
        !modalContainer.contains(document.activeElement) &&
        document.activeElement !== iframeRef.current;
        if (isOutsideModal) {
            e.preventDefault();
            const firstFocusable = modalContainer.querySelector<HTMLElement>(
                'button:not([disabled]), [href]:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
            );
            if (firstFocusable) firstFocusable.focus();
            else if (iframeRef.current) iframeRef.current.focus();
        }
    };

    const handleIframeLoad = () => {
        const iframe = iframeRef.current;
        if (!iframe || !iframe.contentWindow) return;
        try {
            const iframeDoc = iframe.contentWindow.document;
            iframeDoc.addEventListener('keydown', trapFocus);
            const firstFocusable = iframeDoc.querySelector<HTMLElement>(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            if (firstFocusable) {
                setTimeout(() => firstFocusable.focus(), 100);
            }
        } catch (err) {
            console.warn('Cannot access iframe content:', err);
        }
    };

    useEffect(() => {
        const modalContainer = modalRef.current;
        document.addEventListener('keydown', globalFocusTrap, true);
        modalContainer?.addEventListener('keydown', trapFocus);
        if (customEvent && onClose) {
            const closeModalFromIframe = (e: CustomEvent) => {
                if (e.detail.event === customEvent) {
                    onClose();
                }
            };
            (window as any).customEvent = customEvent;
            window.addEventListener(customEvent, closeModalFromIframe);
            return () => {
                delete (window as any).customEvent;
                window.removeEventListener(customEvent, closeModalFromIframe);
                document.removeEventListener('keydown', globalFocusTrap, true);
                modalContainer?.removeEventListener('keydown', trapFocus);
                try {
                    iframeRef.current?.contentWindow?.document?.removeEventListener('keydown', trapFocus);
                } catch (e) {
                // ignore
                }
            };
        }
        return () => {
            document.removeEventListener('keydown', globalFocusTrap, true);
            modalContainer?.removeEventListener('keydown', trapFocus);
            try {
                iframeRef.current?.contentWindow?.document?.removeEventListener('keydown', trapFocus);
            } catch (e) {
                // ignore
            }
        };
    }, [customEvent]);

    return { handleIframeLoad };
};
